import type React from 'react'
import { useState } from 'react'
import styled from 'styled-components'

import TooltipIcon from '@/components/IconGroup/TooltipIcon'
import type { DocumentProperties } from '@/types/Activity'
import { parseDate, toDateLabel } from '@/utils/date'

interface Props {
  properties?: DocumentProperties
}

const formatDate = (createdDate?: string) => {
  if (!createdDate) return 'No date available'

  return toDateLabel({
    date: parseDate(createdDate),
    dateFormat: 'dd/MM/yyyy HH:mm',
  })
}

interface ExpandedProps {
  $isExpanded: boolean
}

const ExpandCollapse = styled.img<ExpandedProps>`
  width: 40px;
  transform: rotate(${props => (props.$isExpanded ? '90deg' : '270deg')});
`

const MetaDataTitle = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;

  &:hover {
    cursor: pointer;
  }
`

const MetaDataItems = styled.div`
  display: grid;
  grid-gap: 2px;
  width: fit-content;
`

const DocumentMetaData: React.FC<Props> = ({ properties }) => {
  const [open, setOpen] = useState(false)

  if (!properties)
    return (
      <div>
        <h3>No metadata available</h3>
      </div>
    )

  return (
    <div>
      <MetaDataTitle onClick={() => setOpen(!open)}>
        <h3>Document metadata</h3>
        <ExpandCollapse $isExpanded={open} src="/assets/svg/collapse.svg" />
      </MetaDataTitle>
      {open && (
        <MetaDataItems>
          <TooltipIcon
            icon={
              <>
                <strong>Author: </strong>
                {properties?.authorName ?? 'Unknown Author'}
              </>
            }
            tooltipMessage="Document author"
          />
          <TooltipIcon
            icon={
              <>
                <strong>Created: </strong>
                {formatDate(properties?.createdDate)}
              </>
            }
            tooltipMessage="Document created date"
          />
          <TooltipIcon
            icon={
              <>
                <strong>Modified: </strong>
                {formatDate(properties?.modifiedDate)}
              </>
            }
            tooltipMessage="Document modified date"
          />
          <TooltipIcon
            icon={
              <>
                <strong>Title: </strong>
                {properties?.documentTitle ?? 'Unknown document title'}
              </>
            }
            tooltipMessage="Document title"
          />
          <TooltipIcon
            icon={
              <>
                <strong>Software: </strong>
                {properties?.softwareUsedToCreate ?? 'Unknown software'}
              </>
            }
            tooltipMessage="Software used to the create document"
          />
        </MetaDataItems>
      )}
    </div>
  )
}

export default DocumentMetaData
